import ForYouTiles from './ForYouTiles/ForYouTiles';
import NavLink from 'components/NavLink';
import OutlinedButton from 'primitives/Buttons/OutlinedButton';
import ReplayIcon from 'styles/icons/Replay';
import Section from 'components/Section';
import SectionHeader from 'primitives/Typography/Headings/SectionHeader';
import SectionLinkPrimitive from 'views/Live/LiveProfile/SectionLink/primitives/SectionLinkPrimitive';
import useMount from 'hooks/useMount';
import useTranslate from 'contexts/TranslateContext/useTranslate';
import { encodePlaylistSeedId } from 'state/Playlist/helpers';
import { fetchInitialRecs } from 'state/Recs/actions';
import {
  getCustomRadioEnabled,
  getForYouEnabled,
} from 'state/Features/selectors';
import { getForYouRecs } from 'state/Recs/selectors';
import { REC_TYPE } from 'state/Recs/constants';
import { shuffle } from 'lodash-es';
import { STATION_TYPE } from 'constants/stationTypes';
import { TILE_RES } from 'components/MediaServerImage';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

type Props = {
  hasExtraPadding?: boolean;
  itemSelectedLocation: string;
  playedFrom: number;
  singleRow?: boolean;
  tilesInRow: number;
};

function NewForYou({
  hasExtraPadding,
  itemSelectedLocation,
  playedFrom,
  singleRow,
  tilesInRow,
}: Props) {
  const dispatch = useDispatch();
  const translate = useTranslate();

  const customRadioEnabled = useSelector(getCustomRadioEnabled);
  const forYouEnabled = useSelector(getForYouEnabled);
  const forYouRecs = useSelector(getForYouRecs);

  const [data, setData] = useState(forYouRecs);
  const [current, setCurrent] = useState(0);

  const limit = singleRow ? tilesInRow : Infinity;
  const onRecsChange = () => {
    const newData =
      shuffle(forYouRecs).map(rec => {
        let { content } = rec;

        // if we are getting a curated playlist
        // we need to add some stuff in for compatibility
        if (
          [REC_TYPE.CURATED, REC_TYPE.N4U].includes(rec.subType) &&
          rec.type === 'DL'
        ) {
          content = {
            ...content,
            seedId: encodePlaylistSeedId(content.ownerId, content.playlistId),
            seedType: STATION_TYPE.COLLECTION,
            url: new URL(
              rec.subType === REC_TYPE.N4U
                ? content.webLink
                : content.contentLink,
            ).pathname,
          };
        }
        const imgPath =
          content?.seedType === STATION_TYPE.FAVORITES
            ? content.imgUrl
            : rec.imagePath;
        return {
          callLetters: content?.callLetters,
          imgUrl: imgPath,
          seedId: content.seedId,
          seedType: content.seedType,
          title: content.name || rec.label,
          url: content.url,
        };
      }) || [];

    setData(newData);
  };
  useMount(() => {
    // Only call this once
    dispatch(fetchInitialRecs());
  });

  useEffect(() => {
    if (forYouRecs.length) {
      onRecsChange();
    }
  }, [forYouRecs]);

  const refreshRecs = () => {
    let newCurrent = current + limit;

    if (newCurrent > data.length) {
      newCurrent = 0;
    }

    setCurrent(newCurrent);
  };

  if (!forYouEnabled || !data.length) return null;

  const recs = data.slice(current, current + limit);

  const header = (
    <SectionHeader hasMobileBottomLink>
      <NavLink rel="nofollow" title={translate('iHeart')} to="/for-you/">
        {translate('For You')}
      </NavLink>
      <SectionLinkPrimitive>
        <OutlinedButton floatRight onClick={refreshRecs} size="small">
          <ReplayIcon height="16" />
          {translate('Refresh')}
        </OutlinedButton>
      </SectionLinkPrimitive>
    </SectionHeader>
  );
  return (
    <Section
      hasBorder={false}
      hasExtraPadding={hasExtraPadding}
      hasMobileBottomLink
      header={header}
    >
      <ForYouTiles
        customRadioEnabled={customRadioEnabled}
        forYouStations={recs}
        hasBottomMargin={false}
        imgWidth={TILE_RES}
        itemSelectedLocation={itemSelectedLocation}
        playedFrom={playedFrom}
        singleRow={singleRow}
        tilesInRow={tilesInRow}
      />
    </Section>
  );
}

export default NewForYou;
